import * as React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  DndContext, PointerSensor, useSensor, useSensors, DragOverlay,
  closestCorners,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowLeft, Plus, Settings2, Zap } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import ListColumn from "@/components/boards/ListColumn";
import CardChip from "@/components/boards/CardChip";
import CardDrawer from "@/components/boards/CardDrawer";
import NewListForm from "@/components/boards/NewListForm";
import AutomationsPanel from "@/components/boards/AutomationsPanel";
import BoardCostHeader from "@/components/boards/BoardCostHeader";

/**
 * BoardView — the kanban. Uses @dnd-kit for drag & drop (cards across
 * lists, and lists themselves). All moves are optimistic and reconciled
 * by SSE.
 */
export default function BoardView() {
  const { companyId, boardId } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { has } = useCompany();

  const canEdit = has("boards.edit");
  const canManage = has("boards.manage");

  const [board, setBoard] = React.useState(null);
  const [lists, setLists] = React.useState([]);
  const [cards, setCards] = React.useState([]);
  const [labels, setLabels] = React.useState([]);
  const [deps, setDeps] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [dragging, setDragging] = React.useState(null);      // {kind:"card"|"list", id}
  const [showAutomations, setShowAutomations] = React.useState(false);

  const openCardId = params.get("card") || null;

  const load = React.useCallback(async () => {
    if (!companyId || !boardId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/boards/${boardId}/hydrate`);
      setBoard(data.board);
      setLists((data.lists || []).sort((a, b) => a.position - b.position));
      setCards(data.cards || []);
      setLabels(data.labels || []);
      setDeps(data.dependencies || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load board");
      nav(`/c/${companyId}/m/boards`);
    } finally {
      setLoading(false);
    }
  }, [companyId, boardId, nav]);

  React.useEffect(() => { load(); }, [load]);

  // Live: reconcile from SSE
  React.useEffect(() => {
    const onEvt = (e) => {
      const evt = e.detail;
      if (evt?.module !== "boards") return;
      if (evt.payload?.board_id && evt.payload.board_id !== boardId) return;
      switch (evt.type) {
        case "card.created":
        case "card.updated":
          setCards((prev) => {
            const c = evt.payload.card;
            const i = prev.findIndex((x) => x.id === c.id);
            if (i === -1) return [...prev, c];
            const copy = [...prev]; copy[i] = { ...copy[i], ...c }; return copy;
          });
          break;
        case "card.moved":
          setCards((prev) => prev.map((x) => x.id === evt.payload.card.id ? { ...x, ...evt.payload.card } : x));
          break;
        case "card.deleted":
          setCards((prev) => prev.filter((x) => x.id !== evt.payload.card_id));
          break;
        case "list.created":
          setLists((prev) => {
            if (prev.some((l) => l.id === evt.payload.list.id)) return prev;
            return [...prev, evt.payload.list].sort((a, b) => a.position - b.position);
          });
          break;
        case "list.updated":
          setLists((prev) => prev.map((l) => l.id === evt.payload.list.id ? { ...l, ...evt.payload.list } : l).sort((a, b) => a.position - b.position));
          break;
        case "list.reordered":
          setLists((prev) => {
            const pos = new Map((evt.payload.order || []).map((o) => [o.id, o.position]));
            return prev.map((l) => pos.has(l.id) ? { ...l, position: pos.get(l.id) } : l)
                       .sort((a, b) => a.position - b.position);
          });
          break;
        case "list.deleted":
          setLists((prev) => prev.filter((l) => l.id !== evt.payload.list_id));
          break;
        case "dependency.created":
          setDeps((prev) => prev.some((d) => d.id === evt.payload.dep.id) ? prev : [...prev, evt.payload.dep]);
          break;
        case "dependency.deleted":
          setDeps((prev) => prev.filter((d) => d.id !== evt.payload.dep_id));
          break;
        case "label.created":
          setLabels((prev) => [...prev, evt.payload.label]);
          break;
        case "label.updated":
          setLabels((prev) => prev.map((l) => l.id === evt.payload.label.id ? evt.payload.label : l));
          break;
        case "label.deleted":
          setLabels((prev) => prev.filter((l) => l.id !== evt.payload.label_id));
          break;
        default: break;
      }
    };
    window.addEventListener("fable:event", onEvt);
    return () => window.removeEventListener("fable:event", onEvt);
  }, [boardId]);

  // Cards indexed by list, sorted by position
  const cardsByList = React.useMemo(() => {
    const map = {};
    for (const l of lists) map[l.id] = [];
    for (const c of cards.filter((x) => !x.archived)) {
      if (!map[c.list_id]) map[c.list_id] = [];
      map[c.list_id].push(c);
    }
    for (const k in map) map[k].sort((a, b) => a.position - b.position);
    return map;
  }, [lists, cards]);

  // Blocked set: for each card, is any of its blockers currently in a non-done list?
  const blockedSet = React.useMemo(() => {
    const doneListIds = new Set(lists.filter((l) => l.is_done).map((l) => l.id));
    const cardById = new Map(cards.map((c) => [c.id, c]));
    const blocked = new Set();
    for (const d of deps) {
      const blocker = cardById.get(d.blocker_card_id);
      if (!blocker) continue;
      if (!doneListIds.has(blocker.list_id)) blocked.add(d.blocked_card_id);
    }
    return blocked;
  }, [deps, lists, cards]);

  // DnD
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const cardIds = React.useMemo(() => cards.map((c) => c.id), [cards]);

  async function moveCard(cardId, targetListId, targetPosition) {
    // Optimistic update
    setCards((prev) => prev.map((c) => c.id === cardId ? { ...c, list_id: targetListId, position: targetPosition } : c));
    try {
      await api.post(`/companies/${companyId}/boards/${boardId}/cards/${cardId}/move`, {
        target_list_id: targetListId,
        target_position: targetPosition,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not move card");
      load();  // reconcile
    }
  }

  function onDragStart(event) {
    const id = String(event.active.id);
    if (id.startsWith("card:")) setDragging({ kind: "card", id: id.slice(5) });
    else if (id.startsWith("list:")) setDragging({ kind: "list", id: id.slice(5) });
  }

  function onDragEnd(event) {
    const { active, over } = event;
    setDragging(null);
    if (!over) return;
    const aId = String(active.id);
    const oId = String(over.id);

    if (aId.startsWith("card:")) {
      const cardId = aId.slice(5);
      const card = cards.find((c) => c.id === cardId);
      if (!card) return;
      let targetListId, targetPosition;
      if (oId.startsWith("list-drop:")) {
        // dropped on the list container (empty area) → last position
        targetListId = oId.slice(10);
        const list = cardsByList[targetListId] || [];
        targetPosition = list.length ? list[list.length - 1].position + 1024 : 1024;
      } else if (oId.startsWith("card:")) {
        const targetCard = cards.find((c) => c.id === oId.slice(5));
        if (!targetCard) return;
        targetListId = targetCard.list_id;
        const list = (cardsByList[targetListId] || []);
        const idx = list.findIndex((c) => c.id === targetCard.id);
        if (idx === -1) return;
        // Place before the target: midpoint between prev and target
        const prev = idx > 0 ? list[idx - 1].position : list[idx].position - 1024;
        // If we're moving inside the same list past the current position of the
        // dragged card, we need to skip over ourselves.
        const filtered = list.filter((c) => c.id !== cardId);
        const targetIdx = filtered.findIndex((c) => c.id === targetCard.id);
        const beforePos = targetIdx > 0 ? filtered[targetIdx - 1].position : filtered[targetIdx].position - 1024;
        const afterPos  = filtered[targetIdx].position;
        targetPosition = (beforePos + afterPos) / 2;
      } else {
        return;
      }
      if (targetListId !== card.list_id || Math.abs(targetPosition - card.position) > 1e-6) {
        moveCard(cardId, targetListId, targetPosition);
      }
    } else if (aId.startsWith("list:")) {
      // list reorder
      const listId = aId.slice(5);
      if (!oId.startsWith("list:")) return;
      const overListId = oId.slice(5);
      if (listId === overListId) return;
      const order = lists.map((l) => l.id);
      const fromIdx = order.indexOf(listId);
      const toIdx = order.indexOf(overListId);
      if (fromIdx === -1 || toIdx === -1) return;
      const newOrder = [...order];
      newOrder.splice(fromIdx, 1);
      newOrder.splice(toIdx, 0, listId);
      // Optimistic
      const posByIndex = newOrder.reduce((acc, id, i) => ({ ...acc, [id]: (i + 1) * 1024 }), {});
      setLists((prev) => prev.map((l) => ({ ...l, position: posByIndex[l.id] })).sort((a, b) => a.position - b.position));
      api.post(`/companies/${companyId}/boards/${boardId}/lists/reorder`, { list_ids: newOrder })
         .catch((e) => { toast.error("Could not reorder"); load(); });
    }
  }

  const openCard = openCardId ? cards.find((c) => c.id === openCardId) : null;

  if (loading || !board) {
    return (
      <div className="h-screen flex flex-col" data-testid="board-loading">
        <div className="px-4 py-3 border-b border-brand-rule bg-card flex items-center gap-3">
          <div className="h-6 w-6 rounded-md bg-brand-rule/60 animate-pulse" />
          <div className="h-4 w-40 bg-brand-rule/60 rounded animate-pulse" />
        </div>
        <div className="flex-1 flex gap-3 px-4 py-4 overflow-hidden">
          {[0,1,2,3].map((i) => (
            <div key={i} className="w-[280px] shrink-0 card-panel p-3 space-y-2">
              <div className="h-3 w-24 bg-brand-rule/60 rounded animate-pulse" />
              <div className="h-16 w-full bg-brand-rule/60 rounded animate-pulse" />
              <div className="h-16 w-full bg-brand-rule/60 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col" data-testid="board-view">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-brand-rule bg-card">
        <div className="flex items-center gap-3 min-w-0">
          <button type="button" onClick={() => nav(`/c/${companyId}/m/boards`)} className="btn-ghost h-8 w-8 !p-0"
                  data-testid="board-back">
            <ArrowLeft size={14} />
          </button>
          <div className="h-6 w-6 rounded-md shrink-0" style={{ background: board.color || "#6E3CFA" }} />
          <div className="min-w-0">
            <div className="text-[15px] font-semibold text-brand-ink truncate flex items-center gap-2">
              {board.name}
              {board.is_operations_board && (
                <span className="chip text-[9.5px] mono-num" data-testid="board-ops-chip">OPS</span>
              )}
            </div>
            <div className="text-[11.5px] text-brand-muted truncate">{board.description || "No description."}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <BoardCostHeader companyId={companyId} boardId={boardId} refreshKey={cards.length} />
          {canManage && (
            <button type="button" className="btn-ghost h-8" onClick={() => setShowAutomations(true)}
                    data-testid="board-open-automations">
              <Zap size={13} /> Automations
            </button>
          )}
        </div>
      </header>

      {/* Kanban */}
      <div className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden" data-testid="board-kanban">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <SortableContext items={lists.map((l) => `list:${l.id}`)} strategy={verticalListSortingStrategy}>
            <div className="flex gap-3 h-full items-start px-4 py-4">
              {lists.map((l) => (
                <ListColumn
                  key={l.id}
                  list={l}
                  cards={cardsByList[l.id] || []}
                  labels={labels}
                  blockedSet={blockedSet}
                  canEdit={canEdit}
                  canManage={canManage}
                  onOpenCard={(c) => setParams({ card: c.id })}
                  companyId={companyId}
                  boardId={boardId}
                />
              ))}
              {canManage && (
                <NewListForm
                  companyId={companyId}
                  boardId={boardId}
                />
              )}
            </div>
          </SortableContext>

          <DragOverlay>
            {dragging?.kind === "card" && (() => {
              const c = cards.find((x) => x.id === dragging.id);
              return c ? <CardChip card={c} labels={labels} blocked={blockedSet.has(c.id)} dragging /> : null;
            })()}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Card detail drawer (RIGHT side) */}
      <CardDrawer
        open={!!openCard}
        onOpenChange={(v) => { if (!v) setParams({}); }}
        card={openCard}
        companyId={companyId}
        boardId={boardId}
        board={board}
        lists={lists}
        labels={labels}
        deps={deps}
        cards={cards}
        canEdit={canEdit}
        canManage={canManage}
        currentUserId={user?.id}
        onUpdated={(c) => setCards((prev) => prev.map((x) => x.id === c.id ? { ...x, ...c } : x))}
        onDeleted={(id) => { setCards((prev) => prev.filter((x) => x.id !== id)); setParams({}); }}
      />

      {/* Automations side panel */}
      {showAutomations && (
        <AutomationsPanel
          onClose={() => setShowAutomations(false)}
          companyId={companyId}
          boardId={boardId}
          lists={lists}
          labels={labels}
          canManage={canManage}
        />
      )}
    </div>
  );
}
